import { useCallback, useLayoutEffect, useMemo, useRef } from "react"
import { useParams } from "@tanstack/react-router"
import { Eip1193Provider } from "ethers"
import { usePostHog } from "posthog-js/react"
import { useAccount } from "wagmi"

import {
  selectMarketTransactionsHistory,
  selectUsername,
  selectWalletAddress,
  useDappSelector,
} from "#/redux-state"

import { MARKET_ITEMS } from "../constants"
import {
  BorrowCollateralChangePosthogEventProps,
  BorrowRepayPosthogEventProps,
  BridgeableTokens,
  BridgeType,
  PosthogEvent,
} from "../types"
import { bigIntToFloat } from "../utils"

import { useIsTahoWallet } from "./custom"

export const usePosthogButtonClickedEvent = () => {
  const posthog = usePostHog()

  const username = useDappSelector(selectUsername)
  const walletAddress = useDappSelector(selectWalletAddress)

  const emitButtonClickedEvent = useCallback(
    (analyticsId: string) => {
      if (!username || !walletAddress) return

      posthog.capture(PosthogEvent.BUTTON_CLICKED, {
        analytics_id: analyticsId,
        username,
        wallet_address: walletAddress,
      })
    },
    [posthog, username, walletAddress],
  )

  return { emitButtonClickedEvent }
}

export const usePosthogWalletConnectedEvent = () => {
  const posthog = usePostHog()
  const loadTimeQS = useRef("")
  const isTahoWallet = useIsTahoWallet()

  useLayoutEffect(() => {
    loadTimeQS.current = window.location.search
  }, [])

  const emitWalletConnectedEvent = useCallback(
    (authId: string, walletAddress: string, username: string) => {
      if (authId) {
        posthog.identify(authId, {
          wallet_address: walletAddress,
          username,
        })
      } else {
        // still anonymous—just capture under the anon ID
      }

      posthog.capture(PosthogEvent.WALLET_CONNECTED, {
        wallet_address: walletAddress,
        username,
      })

      if ("taho" in window && isTahoWallet) {
        const srcParam = new URLSearchParams(loadTimeQS.current).get("src")

        ;(window.taho as Eip1193Provider)
          .request({
            method: "tally_getMezoClaimData",
            params: [],
          })
          .then((response) => {
            const [installId] = response
            if (installId) {
              posthog.capture(PosthogEvent.TAHO_WALLET_CONNECTED, {
                taho_wallet_id: installId,
                wallet_address: walletAddress,
                src: srcParam,
              })
            }
          })
      }
    },
    [posthog, isTahoWallet],
  )

  const emitWalletConnectedErrorEvent = useCallback(() => {
    posthog.capture(PosthogEvent.WALLET_CONNECTED_ERROR)
  }, [posthog])

  return {
    emitWalletConnectedEvent,
    emitWalletConnectedErrorEvent,
  }
}

export const usePosthogSignUpEvent = () => {
  const posthog = usePostHog()

  const emitSignUpStartedEvent = useCallback(() => {
    posthog.capture(PosthogEvent.SIGN_UP_STARTED)
  }, [posthog])

  const emitSignUpCompletedEvent = useCallback(
    (authId: string, walletAddress: string, username: string) => {
      posthog.alias(authId) // alias() merges existing anon profile into authId

      posthog.identify(authId, {
        username,
        primary_wallet_address: walletAddress,
      })

      posthog.capture(PosthogEvent.SIGN_UP_COMPLETED, {
        username,
        primary_wallet_address: walletAddress,
      })
    },
    [posthog],
  )

  const emitSignUpRewardClaimEvent = useCallback(() => {
    posthog.capture(PosthogEvent.SIGN_UP_REWARDS_CLAIMED)
  }, [posthog])

  const emitSignUpDiscordAddedEvent = useCallback(
    (authId: string, discordName: string) => {
      posthog.identify(authId, { discord_name: discordName })
      posthog.capture(PosthogEvent.SIGN_UP_DISCORD_ADDED, {
        discord_name: discordName,
      })
    },
    [posthog],
  )

  return {
    emitSignUpStartedEvent,
    emitSignUpCompletedEvent,
    emitSignUpRewardClaimEvent,
    emitSignUpDiscordAddedEvent,
  }
}

export const usePosthogUsernameSetEvent = () => {
  const posthog = usePostHog()

  const emitUsernameSetEvent = useCallback(
    (authId: string, username: string) => {
      posthog.identify(authId, { username })
      posthog.capture(PosthogEvent.USERNAME_SET, { username })
    },
    [posthog],
  )

  return { emitUsernameSetEvent }
}

export const usePosthogBorrowEvent = () => {
  const posthog = usePostHog()

  const emitBorrowedEvent = useCallback(
    (walletAddress: string, borrowAmount: string, collateralAmount: string) => {
      posthog.capture(PosthogEvent.BORROWED, {
        wallet_address: walletAddress,
        borrow_amount: borrowAmount,
        collateral_amount: collateralAmount,
      })
    },
    [posthog],
  )

  const emitBorrowErrorEvent = useCallback(
    (walletAddress: string, borrowAmount: string, collateralAmount: string) => {
      posthog.capture(PosthogEvent.BORROW_ERROR, {
        wallet_address: walletAddress,
        borrow_amount: borrowAmount,
        collateral_amount: collateralAmount,
      })
    },
    [posthog],
  )

  return { emitBorrowedEvent, emitBorrowErrorEvent }
}

export const usePosthogMatsForBorrowEvent = () => {
  const posthog = usePostHog()

  const emitBorrowMatsClaimedEvent = useCallback(
    (walletAddress: string, matsAmount: number, borrowedAmount: string) => {
      posthog.capture(PosthogEvent.BORROW_MATS_CLAIMED, {
        wallet_address: walletAddress,
        mats_amount: matsAmount,
        borrowed_amount: borrowedAmount,
      })
    },
    [posthog],
  )

  const emitBorrowMatsClaimErrorEvent = useCallback(
    (
      walletAddress: string,
      borrowedAmount: string,
      collateralAmount: string,
    ) => {
      posthog.capture(PosthogEvent.BORROW_MATS_CLAIM_ERROR, {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
        collateral_amount: collateralAmount,
      })
    },
    [posthog],
  )

  return { emitBorrowMatsClaimedEvent, emitBorrowMatsClaimErrorEvent }
}

export const usePosthogBorrowMoreModalEvents = () => {
  const posthog = usePostHog()

  const emitBorrowMoreButtonClickedEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_BUTTON_CLICKED)
  }, [posthog])

  const emitBorrowMoreBalanceConfirmedEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_BALANCE_CONFIRMED)
  }, [posthog])

  const emitBorrowMoreStrategyIncreaseCollEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_STRATEGY_INCREASE_COLL)
  }, [posthog])

  const emitBorrowMoreStrategyReduceRatioEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_STRATEGY_REDUCE_RATIO)
  }, [posthog])

  const emitBorrowMoreConfirmIncreaseCollEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_CONFIRM_INCREASE_COLL)
  }, [posthog])

  const emitBorrowMoreConfirmReduceRatioEvent = useCallback(() => {
    posthog.capture(PosthogEvent.BORROW_MORE_CONFIRM_REDUCE_RATIO)
  }, [posthog])

  return {
    emitBorrowMoreButtonClickedEvent,
    emitBorrowMoreBalanceConfirmedEvent,
    emitBorrowMoreStrategyIncreaseCollEvent,
    emitBorrowMoreStrategyReduceRatioEvent,
    emitBorrowMoreConfirmIncreaseCollEvent,
    emitBorrowMoreConfirmReduceRatioEvent,
  }
}

export const usePosthogBorrowMoreEvent = () => {
  const posthog = usePostHog()

  const emitBorrowMoreIncreaseCollEvent = useCallback(
    (
      walletAddress: string,
      borrowedAmount: string,
      collateralAmount: string,
    ) => {
      posthog.capture(PosthogEvent.BORROW_MORE_INCREASE_COLL, {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
        collateral_amount: collateralAmount,
      })
    },
    [posthog],
  )

  const emitBorrowMoreIncreaseCollErrorEvent = useCallback(
    (
      walletAddress: string,
      borrowedAmount: string,
      collateralAmount: string,
    ) => {
      posthog.capture(PosthogEvent.BORROW_MORE_INCREASE_COLL_ERROR, {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
        collateral_amount: collateralAmount,
      })
    },
    [posthog],
  )

  const emitBorrowMoreReduceRatioEvent = useCallback(
    (walletAddress: string, borrowedAmount: string) => {
      posthog.capture(PosthogEvent.BORROW_MORE_REDUCE_RATIO, {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
      })
    },
    [posthog],
  )

  const emitBorrowMoreReduceRatioErrorEvent = useCallback(
    (walletAddress: string, borrowedAmount: string) => {
      posthog.capture(PosthogEvent.BORROW_MORE_REDUCE_RATIO_ERROR, {
        wallet_address: walletAddress,
        borrow_amount: borrowedAmount,
      })
    },
    [posthog],
  )

  return {
    emitBorrowMoreIncreaseCollErrorEvent,
    emitBorrowMoreIncreaseCollEvent,
    emitBorrowMoreReduceRatioErrorEvent,
    emitBorrowMoreReduceRatioEvent,
  }
}

export const usePosthogRepayEvent = () => {
  const posthog = usePostHog()

  const emitBorrowRepayStartedEvent = useCallback(
    () => posthog.capture(PosthogEvent.BORROW_REPAY_STARTED),
    [posthog],
  )

  const emitBorrowRepayErrorEvent = useCallback(
    (eventProps: BorrowRepayPosthogEventProps) =>
      posthog.capture(PosthogEvent.BORROW_REPAY_ERROR, eventProps),
    [posthog],
  )

  const emitBorrowRepayEvent = useCallback(
    (eventProps: BorrowRepayPosthogEventProps) =>
      posthog.capture(PosthogEvent.BORROW_REPAY, eventProps),
    [posthog],
  )

  return {
    emitBorrowRepayStartedEvent,
    emitBorrowRepayErrorEvent,
    emitBorrowRepayEvent,
  }
}

export const usePosthogCollateralManagementEvent = () => {
  const posthog = usePostHog()

  const emitCollateralDepositStartedEvent = useCallback(
    () => posthog.capture(PosthogEvent.BORROW_COLLATERAL_DEPOSIT_STARTED),
    [posthog],
  )

  const emitCollateralDepositErrorEvent = useCallback(
    () => posthog.capture(PosthogEvent.BORROW_COLLATERAL_DEPOSIT_ERROR),
    [posthog],
  )

  const emitCollateralDepositEvent = useCallback(
    (eventProps: BorrowCollateralChangePosthogEventProps) => {
      posthog.capture(PosthogEvent.BORROW_COLLATERAL_DEPOSIT, eventProps)
    },
    [posthog],
  )

  const emitCollateralWithdrawStartedEvent = useCallback(
    () => posthog.capture(PosthogEvent.BORROW_COLLATERAL_WITHDRAW_STARTED),
    [posthog],
  )

  const emitCollateralWithdrawErrorEvent = useCallback(
    () => posthog.capture(PosthogEvent.BORROW_COLLATERAL_WITHDRAW_ERROR),
    [posthog],
  )

  const emitCollateralWithdrawEvent = useCallback(
    (eventProps: BorrowCollateralChangePosthogEventProps) => {
      posthog.capture(PosthogEvent.BORROW_COLLATERAL_WITHDRAW, eventProps)
    },
    [posthog],
  )

  return {
    emitCollateralDepositStartedEvent,
    emitCollateralDepositErrorEvent,
    emitCollateralDepositEvent,
    emitCollateralWithdrawStartedEvent,
    emitCollateralWithdrawErrorEvent,
    emitCollateralWithdrawEvent,
  }
}

export const usePosthogMatsConvertedEvent = () => {
  const posthog = usePostHog()

  const emitMatsConvertedEvent = useCallback(
    (walletAddress: string, matsAmount: string) => {
      posthog.capture(PosthogEvent.MATS_CONVERTED, {
        wallet_address: walletAddress,
        mats_amount: matsAmount,
      })
    },
    [posthog],
  )

  return { emitMatsConvertedEvent }
}

export const usePosthogMarketTransactionEvent = () => {
  const posthog = usePostHog()
  const account = useAccount()
  const walletAddress = account.address
  const walletProvider = account.connector?.name
  const username = useDappSelector(selectUsername)
  const transactionHistory = useDappSelector(selectMarketTransactionsHistory)
  const isPurchaser = transactionHistory.length > 0
  const { marketItemId } = useParams({ strict: false })

  const itemName = useMemo(() => {
    const itemById = MARKET_ITEMS.find(
      (marketItem) => marketItem.id === marketItemId,
    )

    return itemById?.id
  }, [marketItemId])

  /**
   * Emit event when user fails to purchase an item. Function retrieves user
   * data based on connected wallet.
   */
  const emitTransactionErrorEvent = useCallback(() => {
    if (!itemName) return

    posthog.identify(walletAddress, { username })
    posthog.capture(PosthogEvent.MARKET_TRANSACTION_ERROR, {
      item_name: itemName,
      wallet_address: walletAddress,
      wallet_provider: walletProvider,
    })
  }, [itemName, posthog, walletAddress, username, walletProvider])

  /**
   * Emit event when user successfully purchases an item. Function retrieves
   * user data based on connected wallet.
   */
  const emitTransactionSuccessEvent = useCallback(
    (type: "donation" | "purchase", amount: string | bigint = 0n) => {
      if (!itemName) return

      const eventName =
        type === "donation"
          ? PosthogEvent.MARKET_DONATION_SUCCESS
          : PosthogEvent.MARKET_PURCHASE_SUCCESS

      posthog.identify(walletAddress, { username, is_purchaser: isPurchaser })
      posthog.capture(eventName, {
        item_name: itemName,
        amount: bigIntToFloat(BigInt(amount)),
        wallet_address: walletAddress,
        wallet_provider: walletProvider,
      })
    },
    [isPurchaser, itemName, posthog, username, walletAddress, walletProvider],
  )

  return {
    emitTransactionErrorEvent,
    emitTransactionSuccessEvent,
  }
}

export const usePosthogSwitchToTestnetEvent = () => {
  const posthog = usePostHog()

  const emitSwitchToTestnetEvent = useCallback(() => {
    posthog.capture(PosthogEvent.SWITCH_TO_TESTNET)
  }, [posthog])

  return { emitSwitchToTestnetEvent }
}

export const usePosthogAddFundsEvent = () => {
  const posthog = usePostHog()

  const emitAddFundsStartedEvent = useCallback(() => {
    posthog.capture(PosthogEvent.ADD_FUNDS_STARTED)
  }, [posthog])

  return { emitAddFundsStartedEvent }
}

export const usePosthogBridgeEvent = () => {
  const posthog = usePostHog()

  const emitAddFundsCompletedEvent = useCallback(
    (
      assetType: BridgeableTokens,
      assetAmount: string,
      walletAddress: string,
      bridgeUsed: BridgeType,
      networkFees: string,
      serviceFees: string,
    ) => {
      posthog.capture(PosthogEvent.ADD_FUNDS_COMPLETED, {
        asset_type: assetType,
        asset_amount: assetAmount,
        wallet_address: walletAddress,
        bridge_used: bridgeUsed,
        network_fees: networkFees,
        service_fees: serviceFees,
      })
    },
    [posthog],
  )

  return { emitAddFundsCompletedEvent }
}

export const usePosthogOnboardingEvent = () => {
  const posthog = usePostHog()

  const emitOnboardingButtonClickedEvent = useCallback(
    (analyticsId: string) => {
      posthog.capture(PosthogEvent.BUTTON_CLICKED, {
        analytics_id: analyticsId,
      })
    },
    [posthog],
  )

  return { emitOnboardingButtonClickedEvent }
}
